/**
 * Sysadmin Catalog → Argus Live (AL-C4).
 *
 * `/sysadmin/catalog/argus-live` rail — list of system-scope dashboard
 * templates that sysadmin authors and tenants clone-from-system.
 *
 * Per the founder's PRIMARY RULE on UI parity (sysadmin ↔ tenant),
 * this page intentionally mirrors the tenant `/argus-live` list (card
 * grid, status filter, search) — but uses the sysadmin authoring
 * hooks (`useSysadminSystemDashboards`) rather than the tenant ones.
 * Per the shared-component-with-scope-prop rule, the IDEAL future
 * state is a single component with `scope=`sysadmin`|`tenant``. Per
 * the Library Phase 3+4+5 precedent (PR-J Strategy A), AL-C4 ships
 * the parallel-module shape first and a follow-up consolidates.
 */

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Plus,
  Search,
  Sparkles,
  Loader2,
  LayoutDashboard,
  AlertCircle,
  AlertTriangle,
  Users as UsersIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { EmptyState } from '@/components/ui/empty-state';
import {
  useSysadminSystemDashboards,
  useCreateSysadminSystemDashboard,
  type SystemDashboardStatus,
  type ClassificationTag,
  type SystemDashboard,
} from '@/lib/use-sysadmin-argus-live';

// R5 P0-1 fix (2026-05-13, defensive): list now lives under index route
// ID `/sysadmin/catalog/argus-live/`; parent `catalog.argus-live.tsx`
// is a pure layout with <Outlet/> so detail children can render.
// Not in the audit, but same defective pattern as the catalog leaves.
export const Route = createFileRoute('/sysadmin/catalog/argus-live/')({
  component: SysadminArgusLivePage,
});

type StatusFilter = SystemDashboardStatus | 'all';

interface NewDashboardDraft {
  slug: string;
  title: string;
  description: string;
  icon: string;
  classificationCeiling: ClassificationTag | null;
}

const EMPTY_DRAFT: NewDashboardDraft = {
  slug: '',
  title: '',
  description: '',
  icon: '',
  classificationCeiling: null,
};

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    draft: 'bg-slate-500/10 text-slate-700 dark:text-slate-300',
    published: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
    archived: 'bg-rose-500/10 text-rose-700 dark:text-rose-400',
  };
  return (
    <Badge
      variant="default"
      className={`text-[10px] gap-1 hover:opacity-90 ${
        map[status] ?? map.draft
      }`}
    >
      {status}
    </Badge>
  );
}

function SysadminArgusLivePage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState<NewDashboardDraft | null>(null);

  const includeArchived = statusFilter === 'archived' || statusFilter === 'all';
  const listQuery = useSysadminSystemDashboards({
    status: statusFilter === 'all' ? undefined : statusFilter,
    includeArchived,
    search: search.trim() || undefined,
  });

  const create = useCreateSysadminSystemDashboard();

  const items = listQuery.data?.items ?? [];

  const handleCreate = async () => {
    if (!draft || !draft.slug || !draft.title) return;
    try {
      const result = await create.mutateAsync({
        slug: draft.slug,
        title: draft.title,
        description: draft.description || null,
        icon: draft.icon || null,
        classificationCeiling: draft.classificationCeiling,
      });
      toast.success('Created "' + result.data.slug + '" (draft)');
      setDraft(null);
      navigate({
        to: '/sysadmin/catalog/argus-live/$id',
        params: { id: result.data.id },
      });
    } catch (err) {
      toast.error('Create failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-4">
      <div className="flex items-center gap-3">
        <LayoutDashboard className="h-7 w-7 text-muted-foreground" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Argus Live — System Templates
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline" className="text-xs font-mono">
              §8.4
            </Badge>
            <Badge variant="secondary" className="text-xs">
              v1
            </Badge>
          </div>
        </div>
      </div>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Reference dashboards</CardTitle>
          <CardDescription>
            Author dashboards once at the platform level — tenants clone them
            into their own workspaces. Lifecycle: draft → published → archived.
            Tenants only see published templates in their clone picker.
          </CardDescription>
        </CardHeader>
      </Card>
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 max-w-sm min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search slug or title…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-9 text-sm"
            aria-label="Search dashboards"
          />
        </div>
        <Select
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v as StatusFilter)}
        >
          <SelectTrigger className="w-40 h-9" aria-label="Status filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="draft">Drafts</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-2">
          <Button
            size="sm"
            className="gap-1.5"
            onClick={() => setDraft({ ...EMPTY_DRAFT })}
            data-testid="new-system-dashboard-button"
          >
            <Plus className="h-3.5 w-3.5" /> New Template
          </Button>
        </div>
      </div>
      {/* Grid */}
      {listQuery.isLoading ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground animate-pulse">
            <Loader2 className="h-5 w-5 inline mr-2 animate-spin" />
            Loading…
          </CardContent>
        </Card>
      ) : listQuery.error ? (
        <Card className="border-destructive/30">
          <CardContent className="py-6 text-sm text-destructive flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>Failed to load: {listQuery.error.message}</div>
          </CardContent>
        </Card>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Sparkles}
          title={
            search || statusFilter !== 'all'
              ? 'No templates match these filters'
              : 'No system templates yet'
          }
          body={
            search || statusFilter !== 'all'
              ? 'Try clearing the filters or broadening your search.'
              : 'Create your first system template. Tenants will see it in their clone picker once you publish.'
          }
          action={
            !search && statusFilter === 'all' ? (
              <Button
                size="sm"
                onClick={() => setDraft({ ...EMPTY_DRAFT })}
                data-testid="empty-create-template-button"
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                Create your first template
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setSearch('');
                  setStatusFilter('all');
                }}
              >
                Clear filters
              </Button>
            )
          }
        />
      ) : (
        <div
          className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          data-testid="sysadmin-argus-live-grid"
        >
          {items.map((d) => (
            <SystemDashboardCard key={d.id} dashboard={d} />
          ))}
        </div>
      )}
      {/* Create drawer */}
      <Sheet
        open={draft !== null}
        onOpenChange={(open) => !open && setDraft(null)}
      >
        <SheetContent
          side="right"
          className="sm:max-w-2xl overflow-y-auto"
          data-testid="new-template-drawer"
        >
          <SheetHeader>
            <SheetTitle>New system template</SheetTitle>
            <SheetDescription>
              Templates are created in draft. Edit the content in the detail
              page, then publish to make it available for tenant clone.
            </SheetDescription>
          </SheetHeader>
          {draft && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="d-slug" className="text-xs">
                    Slug (immutable)
                  </Label>
                  <Input
                    id="d-slug"
                    value={draft.slug}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        slug: e.target.value
                          .toLowerCase()
                          .replace(/[^a-z0-9_.-]/g, '-'),
                      })
                    }
                    placeholder="manufacturing-ops"
                    className="font-mono text-xs h-8"
                    data-testid="template-slug-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="d-icon" className="text-xs">
                    Icon (lucide-react name)
                  </Label>
                  <Input
                    id="d-icon"
                    value={draft.icon}
                    onChange={(e) =>
                      setDraft({ ...draft, icon: e.target.value })
                    }
                    placeholder="LayoutDashboard"
                    className="text-xs h-8"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="d-title" className="text-xs">
                  Title
                </Label>
                <Input
                  id="d-title"
                  value={draft.title}
                  onChange={(e) =>
                    setDraft({ ...draft, title: e.target.value })
                  }
                  placeholder="Manufacturing operations overview"
                  className="text-sm h-8"
                  data-testid="template-title-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="d-desc" className="text-xs">
                  Description
                </Label>
                <Textarea
                  id="d-desc"
                  value={draft.description}
                  onChange={(e) =>
                    setDraft({ ...draft, description: e.target.value })
                  }
                  placeholder="A reference dashboard showing live equipment status, MTBF, and shift handover."
                  rows={3}
                  className="text-sm"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Classification ceiling (optional)</Label>
                <div className="grid grid-cols-2 gap-3">
                  <Select
                    value={draft.classificationCeiling?.sensitivity ?? 'none'}
                    onValueChange={(v) =>
                      setDraft({
                        ...draft,
                        classificationCeiling:
                          v === 'none'
                            ? null
                            : {
                                sensitivity:
                                  v as ClassificationTag['sensitivity'],
                                exportControl:
                                  draft.classificationCeiling?.exportControl ??
                                  'none',
                              },
                      })
                    }
                  >
                    <SelectTrigger
                      className="h-8 text-xs"
                      aria-label="Sensitivity ceiling"
                    >
                      <SelectValue placeholder="No ceiling" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No ceiling</SelectItem>
                      <SelectItem value="unclassified">unclassified</SelectItem>
                      <SelectItem value="cui">cui</SelectItem>
                      <SelectItem value="restricted">restricted</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select
                    value={draft.classificationCeiling?.exportControl ?? 'none'}
                    onValueChange={(v) =>
                      setDraft({
                        ...draft,
                        classificationCeiling: draft.classificationCeiling
                          ? {
                              ...draft.classificationCeiling,
                              exportControl:
                                v as ClassificationTag['exportControl'],
                            }
                          : v === 'none'
                          ? null
                          : {
                              sensitivity: 'unclassified',
                              exportControl:
                                v as ClassificationTag['exportControl'],
                            },
                      })
                    }
                    disabled={!draft.classificationCeiling}
                  >
                    <SelectTrigger
                      className="h-8 text-xs"
                      aria-label="Export control ceiling"
                    >
                      <SelectValue placeholder="none" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">none</SelectItem>
                      <SelectItem value="ear">ear</SelectItem>
                      <SelectItem value="itar">itar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-[10px] text-muted-foreground">
                  Templates with a ceiling cannot be cloned by tenants whose
                  compliance profile is below this level.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDraft(null)}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleCreate}
                  data-testid="template-create-submit"
                  disabled={
                    !draft.slug || !draft.title || create.isPending
                  }
                >
                  {create.isPending ? 'Creating…' : 'Create draft'}
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  )
}

function SystemDashboardCard({ dashboard }: { dashboard: SystemDashboard }) {
  return (
    <Link
      to="/sysadmin/catalog/argus-live/$id"
      params={{ id: dashboard.id }}
      data-testid={`sysadmin-argus-live-card-${dashboard.slug}`}
      className="block"
    >
      <Card className="hover:shadow-md hover:border-primary/40 transition-all cursor-pointer">
        <CardContent className="p-4 flex flex-col gap-2">
          <div className="flex items-start justify-between gap-2">
            <h3
              className="font-semibold text-sm truncate flex-1"
              title={dashboard.title}
            >
              {dashboard.title}
            </h3>
            <StatusBadge status={dashboard.status} />
          </div>
          <Badge variant="outline" className="text-[9px] font-mono w-fit">
            {dashboard.slug}
          </Badge>
          {dashboard.description ? (
            <p
              className="text-xs text-muted-foreground line-clamp-2"
              title={dashboard.description}
            >
              {dashboard.description}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground/60 italic">
              No description
            </p>
          )}
          <div className="flex items-center justify-between mt-1 text-[10px] text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <UsersIcon className="h-3 w-3" />
              {dashboard.cloneCount} clone
              {dashboard.cloneCount === 1 ? '' : 's'}
            </span>
            {dashboard.classificationCeiling && (
              <Badge variant="outline" className="text-[9px]">
                {dashboard.classificationCeiling.sensitivity} /{' '}
                {dashboard.classificationCeiling.exportControl}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
